import React, { useState, useEffect } from 'react';
import { Send, Trash2, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { tagApi } from '../api';

const PostForm = ({ onSubmit }) => {
  const [content, setContent] = useState('');
  const [location, setLocation] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showShredder, setShowShredder] = useState(false);
  const [presetTags, setPresetTags] = useState([]);

  const defaultTags = [
    'PUA大师', '薪资倒挂', '外行领导内行', '画饼专家',
    '加班文化', '职场霸凌', '无效会议', '甩锅专家',
    '微管理', '朝令夕改', '不给资源要结果', '形式主义'
  ];

  useEffect(() => {
    tagApi.getPreset()
      .then(tags => setPresetTags(tags.length > 0 ? tags : defaultTags))
      .catch(() => setPresetTags(defaultTags));
  }, []);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (content.trim()) {
      onSubmit({
        content: content.trim(),
        location: location.trim() || undefined,
        tags: selectedTags
      });
      setContent('');
      setLocation('');
      setSelectedTags([]);
    }
  };

  const handleShred = () => {
    setShowShredder(true);
    setTimeout(() => {
      setContent('');
      setLocation('');
      setSelectedTags([]);
      setShowShredder(false);
    }, 2500);
  };

  const tagsToShow = presetTags.length > 0 ? presetTags : defaultTags;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-terminal-green mb-2 flex items-center gap-2">
          <Tag className="w-5 h-5" />
          匿名吐槽区
        </h3>
        <p className="text-sm text-slate-400">
          在这里释放你的职场压力，我们保护你的隐私
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Content Input */}
        <div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="说出你的职场遭遇吧... (匿名发布，请放心)"
            className="w-full h-32 bg-slate-900 border border-slate-600 rounded-lg p-4 text-slate-200 placeholder-slate-500 focus:border-terminal-green focus:outline-none focus:ring-2 focus:ring-terminal-green/20 resize-none"
            maxLength={500}
          />
          <div className="text-right text-xs text-slate-500 mt-1">
            {content.length}/500
          </div>
        </div>

        {/* Location Input */}
        <div>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="位置描述 (可选，如：某互联网大厂)"
            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 placeholder-slate-500 focus:border-terminal-green focus:outline-none"
            maxLength={100}
          />
        </div>

        {/* Tag Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            选择标签 (可多选)
          </label>
          <div className="flex flex-wrap gap-2">
            {tagsToShow.map(tag => (
              <motion.button
                key={tag}
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-all ${selectedTags.includes(tag)
                  ? 'bg-green-900 text-terminal-green border border-green-500'
                  : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
              >
                #{tag}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleShred}
            disabled={!content.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-500 shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Trash2 className="w-4 h-4" />
            碎纸机 (仅释放压力)
          </motion.button>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!content.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-terminal-green text-slate-900 rounded-lg font-semibold hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Send className="w-4 h-4" />
            提交离职申请 (发布)
          </motion.button>
        </div>
      </form>

      <AnimatePresence>
        {showShredder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center z-50 overflow-hidden"
          >
            <div className="relative w-full max-w-lg p-8">
              {/* Shredder Entrance */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-4 bg-slate-700 rounded-full border-2 border-slate-600 shadow-lg z-10">
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent"></div>
              </div>

              {/* Fragment Container */}
              <div className="text-center min-h-[300px] flex flex-wrap justify-center gap-1 p-12">
                {content.split('').map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 1, y: 0, x: 0, rotate: 0, scale: 1 }}
                    animate={{
                      opacity: [1, 1, 0],
                      y: [0, 50, 400],
                      x: [0, (Math.random() - 0.5) * 50, (Math.random() - 0.5) * 300],
                      rotate: [0, Math.random() * 180, Math.random() * 720 - 360],
                      scale: [1, 0.8, 0.4]
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.005,
                      ease: "easeIn"
                    }}
                    className="inline-block text-slate-300 font-mono text-xl whitespace-pre select-none"
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </motion.span>
                ))}
              </div>

              {/* Status Message */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="text-center mt-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900/50 border border-indigo-500/30 rounded-full text-indigo-400">
                  <Trash2 className="w-4 h-4 animate-bounce" />
                  <span className="text-sm font-medium">压力正在粉碎中...</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PostForm;